import {
  Box,
  Checkbox,
  FieldMessage,
  Heading,
  RadioGroup,
  RadioItem,
  Stack,
  Text,
  TextField,
  Tiles,
} from 'braid-design-system';
import { useFormContext } from 'react-hook-form';

import { LargestCustomers } from '../LargestCustomers';

export const BusinessDetails = () => {
  const {
    register,
    formState: { errors },
    watch,
    setValue,
    trigger,
  } = useFormContext();

  const partnerTypes: string[] = watch('partnerType');

  return (
    <Stack space="medium">
      <Box paddingTop="gutter">
        <Heading level="3">Business details</Heading>
      </Box>
      <TextField
        id="company"
        label="Company"
        onClear={() => setValue('company', '')}
        message={errors.company?.message as string}
        tone={Boolean(errors.company) ? 'critical' : 'neutral'}
        {...register('company')}
        value={watch('company')}
      />
      <TextField
        id="companyUrl"
        label="Company website URL"
        onClear={() => setValue('company', '')}
        message={errors.companyUrl?.message as string}
        tone={Boolean(errors.companyUrl) ? 'critical' : 'neutral'}
        {...register('companyUrl')}
        value={watch('companyUrl')}
      />

      <Stack space="medium">
        <Text weight="strong">Partner type</Text>
        <Text>Please select at least one.</Text>

        <Tiles columns={2} space="small">
          {['ATS', 'Multiposter', 'CRM', 'HRIS', 'SEEK customer', 'Other'].map(
            (type) => {
              const checked = partnerTypes.includes(type);
              return (
                <Checkbox
                  key={type}
                  id={`checkbox-${type.toLocaleLowerCase()}`}
                  checked={checked}
                  onChange={(e) => {
                    const newValue = [
                      ...partnerTypes.filter((t) => t !== type),
                      ...(e.currentTarget.checked ? [type] : []),
                    ];

                    setValue('partnerType', newValue);
                    register('partnerType').onChange({
                      target: { value: newValue },
                    });
                    trigger('partnerType');
                  }}
                  label={type}
                  value={type}
                  tone={Boolean(errors.partnerType) ? 'critical' : 'neutral'}
                />
              );
            },
          )}
        </Tiles>

        {Boolean(errors.partnerType) ? (
          <FieldMessage
            id="partnerType"
            message="Select at least one solution"
            tone="critical"
          />
        ) : null}
      </Stack>

      <RadioGroup
        label="Are you a recruitment software provider wanting to integrate to SEEK?"
        id="seekRsp"
        onChange={({ currentTarget: { value } }) => {
          if (value === 'yes') {
            setValue('company', '');
          }
          setValue('seekRsp', value as 'yes' | 'no');
          register('seekRsp').onChange({ target: { value } });
        }}
        value={watch('seekRsp') || 'yes'}
      >
        <RadioItem label="Yes" value="yes" />
        <RadioItem label="No" value="no" />
      </RadioGroup>
      {watch('seekRsp') === 'no' && (
        <TextField
          id="recruitmentSoftware"
          label="What recruitment software do you use?"
          onClear={() => setValue('recruitmentSoftware', '')}
          message={errors.recruitmentSoftware?.message as string}
          tone={Boolean(errors.recruitmentSoftware) ? 'critical' : 'neutral'}
          {...register('recruitmentSoftware')}
          value={watch('recruitmentSoftware') || ''}
        />
      )}
      {watch('seekRsp') === 'yes' && (
        <>
          <TextField
            id="customerSegment"
            label="If you are a recruitment software provider, what is the primary customer segment your product serves?"
            onClear={() => setValue('customerSegment', '')}
            message={errors.customerSegment?.message as string}
            tone={Boolean(errors.customerSegment) ? 'critical' : 'neutral'}
            {...register('customerSegment')}
            value={watch('customerSegment') || ''}
          />
          <TextField
            id="numCustomers"
            label="How many SEEK customers do you have?"
            onClear={() => setValue('numCustomers', '')}
            message={errors.numCustomers?.message as string}
            tone={Boolean(errors.numCustomers) ? 'critical' : 'neutral'}
            type="number"
            {...register('numCustomers')}
            value={watch('numCustomers')}
          />
          {Number(watch('numCustomers')) > 0 && (
            <LargestCustomers
              customersLength={Number(watch('numCustomers'))}
              error={Boolean(errors.largestCustomers)}
              id="largestCustomers"
              // No need to pass ref here
              rawValue={watch('largestCustomers') || ''}
              onChange={(value) => {
                setValue('largestCustomers', value);
                register('largestCustomers').onChange({ target: { value } });
                trigger('largestCustomers');
              }}
            />
          )}
        </>
      )}
    </Stack>
  );
};
